import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { useInviteUser } from 'src/features/invites/api/use-invite-user'
import { FormTipIcon } from 'src/shared/components/icons/form-tip-icon'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().length(14, 'Phone number must be 10 numbers').optional().or(z.literal('')),
})

type InviteFormData = z.infer<typeof inviteSchema>

interface SendInviteFormProps {
  onSuccess?: () => void
  className?: string
}

export const SendInviteForm = ({ onSuccess, className }: SendInviteFormProps) => {
  const { toast } = useToast()
  const { mutate: inviteUser, isPending } = useInviteUser()

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      phone: undefined,
    },
  })

  const onSubmit = (data: InviteFormData) => {
    inviteUser(
      { email: data.email, phone: data.phone || undefined },
      {
        onSuccess: () => {
          toast({
            title: 'Invite sent!',
            description: `An invitation has been sent to ${data.email}`,
          })
          form.reset()
          onSuccess?.()
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: 'Failed to send invite',
            description: 'Please try again later',
          })
        },
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={cn(className, 'flex flex-col justify-between gap-4')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            {' '}
            <h3 className="text-2xl font-bold">Send Invite</h3>
            <p className="text-start text-sm">
              You and your invitee earn 10
              <PointIcon className="mx-1 inline-block size-4 align-text-bottom" />
              when they join and verify!
            </p>{' '}
          </div>

          <div className="space-y-1">
            <InputFormField
              control={form.control}
              name="email"
              label="Email address"
              type="email"
              disabled={isPending}
              className="w-full"
            />
            <InputFormField
              control={form.control}
              name="phone"
              label="Phone (optional)"
              type="tel"
              disabled={isPending}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {' '}
          <div className="flex justify-start gap-2">
            <FormTipIcon className="size-4" />
            <p className="text-sm font-bold">A phone number is recommended!</p>
          </div>
          <Button type="submit" disabled={!form.watch().email} isLoading={isPending} className="w-full">
            Send Invite
          </Button>
        </div>
      </form>
    </Form>
  )
}
